/**
 * OpenShiftCard (BON-242) — one eligible shift on the teacher's board.
 *
 * Pay reads em-dash: role-based shift rates are BON-245. The design shows a
 * figure here, but inventing one would be worse than admitting we don't know
 * it yet — the whole point of its "pay reads with its reason" note is that the
 * number must never look wrong.
 */
import { crewPositionLabel } from '../../../lib/crewPositions';
import { formatShiftStart } from '../regional/shiftFormat';

import type { EligibleShiftEntry } from './teacherApi';

const EM_DASH = '—';

export interface OpenShiftCardProps {
  shift: EligibleShiftEntry;
  onRequest: (id: string) => void;
  onWithdraw: (id: string) => void;
  busy: boolean;
}

export function OpenShiftCard({ shift, onRequest, onWithdraw, busy }: OpenShiftCardProps) {
  const requested = shift.myRequestStatus === 'pending';
  const label = crewPositionLabel(shift.position);
  const others = requested ? shift.requestCount - 1 : shift.requestCount;

  return (
    <article
      className="nb-card"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        borderColor: requested ? 'var(--nb-primary)' : undefined,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--nb-primary)' }}>{label}</span>
        <span style={{ fontSize: 11, color: 'var(--nb-subtle)' }}>
          {formatShiftStart(shift.startsAtIso)}
        </span>
      </div>

      <div>
        <div style={{ fontSize: 13, fontWeight: 700 }}>{shift.venueShort}</div>
        <div style={{ fontSize: 11.5, color: 'var(--nb-muted)' }}>{shift.workshopTypeName}</div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {/* TODO(BON-245): role-based shift pay rates. */}
        <span className="nb-mono" style={{ fontSize: 12, color: 'var(--nb-subtle)' }}>
          {EM_DASH}
        </span>
        <span style={{ fontSize: 11, color: 'var(--nb-faint)' }}>
          {others > 0
            ? `${others} other${others === 1 ? '' : 's'} waiting`
            : requested
              ? 'You’re first in line'
              : 'Be the first to request'}
        </span>
      </div>

      {requested ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 11.5, color: 'var(--nb-primary)' }}>✓ Requested</span>
          <button
            type="button"
            className="nb-btn nb-btn-ghost"
            disabled={busy}
            onClick={() => onWithdraw(shift.id)}
          >
            Withdraw
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="nb-btn nb-btn-primary"
          disabled={busy}
          onClick={() => onRequest(shift.id)}
        >
          Request shift
        </button>
      )}
    </article>
  );
}
